import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Disclosure } from "@headlessui/react";
import Loading from "../../../../components/Loaders/Loading";
import DateInput from "../../../../components/Inputs/DateInput";
import Select from "../../../../components/Inputs/Select";
import Button from "../../../../components/Button";
import HistoryContent from "./HistoryContent";
import LedgerContent from "./LedgerContent";
import { ButtonGroup } from "reactstrap";
import { useAccess } from "../../../../utils/hooks/access";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faInfo } from "@fortawesome/free-solid-svg-icons";
import { useTimeOffHistoryApi } from "./api";
import { firstToUpper } from "../../../../utils/helpers/string";
import "./style.scss";
import { ProfileContext } from "../../context";
import { useVisible } from "../../../../utils/hooks/useVisible";
import InfoModal from "../../TimeOff/InfoModal";
import { StatusCell } from "../helper";

const types = [
    // "approve-request",
    // "approve-cancel-request",
    "assign",
    "unassign",
    "lock-pay-group",
    "unlock-pay-group",
    "update-total",
    "transfer-assign",
    "transfer-unassign",
    "payout"
];

// The open plan panel, switchable between the log narrative (History) and the balance ledger.
const PlanPanel = ({ plan, filters }) => {
    const { t } = useTranslation();
    const { isSelf } = useContext(ProfileContext);
    const { hasAccess: canViewLedger } = useAccess(
        isSelf ? "profile.viewTimeOffHistory" : "people.viewTimeOffHistory"
    );
    // the ledger is the default view; without the permission the History log shows instead
    const [view, setView] = useState("ledger");

    const toolbar = canViewLedger ? (
        <div className="d-flex justify-content-end mb-2">
            <ButtonGroup size="sm">
                <Button
                    size="sm"
                    color={view === "history" ? "primary" : "secondary"}
                    onClick={() => setView("history")}
                >
                    {t("history")}
                </Button>
                <Button
                    size="sm"
                    color={view === "ledger" ? "primary" : "secondary"}
                    onClick={() => setView("ledger")}
                >
                    {t("ledger")}
                </Button>
            </ButtonGroup>
        </div>
    ) : null;

    return canViewLedger && view === "ledger" ? (
        <LedgerContent plan={plan} toolbar={toolbar} />
    ) : (
        <HistoryContent plan={plan} filters={filters} toolbar={toolbar} />
    );
};

const TimeoffHistory = () => {
    const { t } = useTranslation();
    const { getPlans, loadingPlans, plans } = useTimeOffHistoryApi();
    const { visible, open: openModal, close, selected: selectedPendingItems } = useVisible();
    const { isSelf, user } = useContext(ProfileContext);
    const [filters, setFilters] = useState({
        year: moment().year(),
    });
    const [error, setError] = useState(null);

    const onYearChange = useCallback((value) => {
        const year = moment(value).year();
        setFilters((prev) => ({
            ...prev,
            from: prev?.from && moment(prev.from).year(year),
            to: prev?.to && moment(prev.to).year(year),
            year,
        }));
    }, []);

    const onStartDateChange = useCallback(
        (value) => {
            setFilters((prev) => ({ ...prev, from: moment(value).year(filters.year) }));
            setError(null);
        },
        [filters.year]
    );

    const onEndDateChange = useCallback(
        (value) => {
            if (!filters?.from) {
                setError(t("required-from"));
            } else {
                setFilters((prev) => ({ ...prev, to: moment(value).year(filters.year) }));
                setError(null);
            }
        },
        [filters.year, filters?.from, t]
    );

    const clearFilters = useCallback(() => {
        setFilters({ year: moment().year() });
    }, []);

    const onTypeChange = useCallback((value) => {
        setFilters((prev) => ({ ...prev, type: value }));
    }, []);

    useEffect(() => {
        // another employee's profile: wait for the user, or a refresh asks for /users/undefined/time-off
        if (!isSelf && !user?.id) return;
        const controller = new AbortController();

        const url = isSelf ? "/profile/time-off/plan" : `/users/${user.id}/time-off`;
        getPlans(url, controller, filters);

        return () => controller.abort();
    }, [getPlans, isSelf, filters, user?.id]);

    return loadingPlans ? (
        <div className="d-flex justify-content-center mt-6 w-100">
            <Loading />
        </div>
    ) : (
        <div>
            <div className="d-flex justify-content-end mb-4 gap-3">
                <div>
                    <DateInput
                        className="w-100 px-4"
                        placeholderText={t("year")}
                        showYearPicker
                        dateFormat="yyyy"
                        onChange={onYearChange}
                        value={moment().year(filters.year).toDate()}
                        customInput={
                            <div>{moment().year(filters.year).format("YYYY")}</div>
                        }
                    />
                </div>
                {/* <div>
                    <DateInput
                        placeholderText={t("from")}
                        onChange={onStartDateChange}
                        value={filters?.from && moment(filters.from).toDate()}
                    />
                </div>
                <div className="d-flex flex-column">
                    <DateInput
                        placeholderText={firstToUpper(t("to"))}
                        onChange={onEndDateChange}
                        value={filters?.to && moment(filters.to).toDate()}
                    />
                    {error && <small className="text-danger">{error}</small>}
                </div> */}
                {/* <div>
                    <Select placeholder={t("type")} onChange={onTypeChange} value={filters?.type}>
                        {types.map((type) => {
                            return (
                                <Select.Option key={type} value={type}>
                                    {t(type)}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </div> */}
                {/* <Button className="text-nowrap" color="primary" onClick={clearFilters}>
                    {t("clear-filters")}
                </Button> */}
            </div>
            <div className="d-flex flex-column justify-content-center align-items-center">
                {plans?.map((plan) => {
                    return (
                        <Disclosure key={plan.id} defaultOpen={false}>
                            {({ open }) => {
                                return (
                                    <div className="history-accordion-container">
                                        <Disclosure.Button className="history-accordion-button">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center justify-content-between ml-3 gap-3 w-100 mr-3">
                                                    <div
                                                        className="d-flex flex-column align-items-start"
                                                        style={{ width: "200px" }}
                                                    >
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <h3 className="mb-0">{plan.plan.code}</h3>
                                                            <StatusCell value={plan.status} t={t} />
                                                        </div>
                                                        <h5 className="text-left font-weight-light mb-0">
                                                            {plan.plan.description}
                                                        </h5>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center w-100">
                                                        <h4 className="mb-0 d-flex flex-column align-items-start border-right pr-2 border-dark-grey">
                                                            {t("initial-balance")}:{" "}
                                                            <span className="font-weight-light text-dark">
                                                                {Number(plan.bucket?.initial).toFixed(2)}
                                                            </span>
                                                        </h4>
                                                        <h4 className="mb-0 d-flex flex-column align-items-start border-right pr-2 border-dark-grey">
                                                            {t("added")}:{" "}
                                                            <span className="font-weight-light text-dark">
                                                                {Number(plan.bucket?.updates).toFixed(2)}
                                                            </span>
                                                        </h4>
                                                        <h4 className="mb-0 d-flex flex-column align-items-start border-right pr-2 border-dark-grey">
                                                            {t("accrued")}:{" "}
                                                            <span className="font-weight-light text-dark">
                                                                {Number(plan.bucket?.earned).toFixed(2)}
                                                            </span>
                                                        </h4>
                                                        <h4 className="mb-0 d-flex flex-column align-items-start border-right pr-2 border-dark-grey">
                                                            {t("pending")}:{" "}
                                                            <span className="font-weight-light text-dark">
                                                                {Number(plan.bucket.pending).toFixed(2)}
                                                                {!Array.isArray(plan.pendingItems) || plan.pendingItems.length === 0 ? null : (
                                                                    <span className="text-muted text-sm ml-2">
                                                                        <FontAwesomeIcon
                                                                            className="text-primary cursor-pointer px-2  py-1"
                                                                            icon={faInfo}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                openModal(plan.pendingItems);
                                                                            }}
                                                                        />
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </h4>

                                                        <h4 className="mb-0 d-flex flex-column align-items-start border-right pr-2 border-dark-grey">
                                                            {t("taken")}:{" "}
                                                            <span className="font-weight-light text-dark">
                                                                {Number(plan.bucket.consumed).toFixed(2)}
                                                            </span>
                                                        </h4>

                                                        <h4 className="mb-0 d-flex flex-column align-items-start border-right pr-2 border-dark-grey">
                                                            {t("available")}:{" "}
                                                            <span className="font-weight-light text-dark">
                                                                {Number(plan.bucket.available).toFixed(2)}
                                                            </span>
                                                        </h4>

                                                        <h4 className="mb-0 d-flex flex-column align-items-start border-right pr-2 border-dark-grey">
                                                            {t("total")}:{" "}
                                                            <span className="font-weight-light text-dark">
                                                                {Number(plan.bucket.total).toFixed(2)}
                                                            </span>
                                                        </h4>
                                                    </div>
                                                </div>
                                                <FontAwesomeIcon
                                                    icon={open ? faChevronUp : faChevronDown}
                                                    className="mr-3"
                                                />
                                            </div>
                                        </Disclosure.Button>
                                        {open && <PlanPanel plan={plan} filters={filters} />}
                                    </div>
                                );
                            }}
                        </Disclosure>
                    );
                })}
                </div>

                <InfoModal
                    values={selectedPendingItems}
                    isOpen={visible}
                    close={close}
                />
        </div>
    );
};

export default TimeoffHistory;
